export const CHUNK_RELOAD_KEY = 'petronexus:chunk-reload-attempted';

const RECOVERY_QUERY_PARAM = 'reload';

const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'failed to fetch module',
  'importing a module script failed',
  'loading chunk',
  'chunkloaderror',
  'unable to preload css',
  'unable to preload js',
  'unable to preload',
];

export const extractErrorMessage = (value: unknown): string => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  return String(value);
};

export const isChunkLoadError = (value: unknown): boolean => {
  const message = extractErrorMessage(value).toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
};

const hasReloadMarker = (url: URL): boolean => url.searchParams.has(RECOVERY_QUERY_PARAM);

export const hasRecentChunkRecoveryReloadAttempt = (maxAgeMs = 2_500): boolean => {
  if (typeof window === 'undefined') return false;

  const url = new URL(window.location.href);
  if (hasReloadMarker(url)) {
    return false;
  }

  try {
    const value = window.sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (!value) return false;
    if (value === '1') return true;

    const attemptedAt = Number(value);
    return Number.isFinite(attemptedAt) && Date.now() - attemptedAt <= maxAgeMs;
  } catch {
    return false;
  }
};

const startChunkRecoveryReload = (force: boolean): boolean => {
  if (typeof window === 'undefined') return false;

  const url = new URL(window.location.href);
  const alreadyTriedViaUrl = hasReloadMarker(url);

  try {
    if (!force && window.sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
      return false;
    }
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    if (!force && alreadyTriedViaUrl) {
      return false;
    }
  }

  url.searchParams.set(RECOVERY_QUERY_PARAM, Date.now().toString());
  window.location.replace(url.toString());
  return true;
};

export const attemptChunkRecoveryReload = (): boolean => startChunkRecoveryReload(false);

export const forceChunkRecoveryReload = (): boolean => startChunkRecoveryReload(true);

export const clearChunkRecoveryReloadFlag = (delayMs = 10_000): number | undefined => {
  if (typeof window === 'undefined') return undefined;

  return window.setTimeout(() => {
    try {
      window.sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    } catch {
      // Storage can be unavailable in hardened browser modes; the URL marker
      // remains enough to stop reload loops until the page has survived boot.
    }

    const url = new URL(window.location.href);
    if (!hasReloadMarker(url)) return;

    url.searchParams.delete(RECOVERY_QUERY_PARAM);
    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(window.history.state, document.title, nextUrl);
  }, delayMs);
};
